import React from 'react';
import { useTranslation } from 'react-i18next';

import { OrthophotoBlending, ViewSettings } from '../../../../../../viewer/view-settings';
import { useUnitSymbols } from '../../../../../hooks/use-unit-symbols';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Select } from '../../../../select/select';
import { Slider } from '../../../../slider/slider';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';
import { useViewSettings } from './use-view-settings';

type Props = {
  /** Altitude only shows in the flexible viewing mode's perspective camera. */
  showAltitude: boolean;
  altitudeDisabled: boolean;
};

const OrthophotoSettings = (props: Props) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const { orthophoto } = useViewSettings();
  const [altitudeInput, setAltitudeInput] = React.useState(orthophoto.altitude.toString());

  return (
    <Stack direction="column" spacing={1}>
      <LabelledContainer
        noWrap={true}
        text={t('viewSettings.orthophoto.blending.title', { ns: 'skyviewToolbar' })}
      >
        {(formElementId) => (
          <Select
            id={formElementId()}
            options={[
              {
                id: 'smooth',
                name: t('viewSettings.orthophoto.blending.smooth', { ns: 'skyviewToolbar' }),
              },
              {
                id: 'pixelated',
                name: t('viewSettings.orthophoto.blending.pixelated', { ns: 'skyviewToolbar' }),
              },
            ]}
            value={orthophoto.blending}
            onChange={(e) =>
              ViewSettings.instance.setOrthophoto({
                blending: e.target.value as OrthophotoBlending,
              })
            }
          />
        )}
      </LabelledContainer>

      <LabelledContainer
        noWrap={true}
        text={t('viewSettings.opacity.title', {
          ns: 'skyviewToolbar',
          value: orthophoto.opacity,
        })}
      >
        <Slider
          max={1}
          min={0}
          step={0.05}
          value={orthophoto.opacity}
          onChange={(opacity) => ViewSettings.instance.setOrthophoto({ opacity })}
        />
      </LabelledContainer>

      {props.showAltitude && (
        <LabelledContainer
          noWrap={true}
          text={t('viewSettings.orthophoto.altitude.title', {
            ns: 'skyviewToolbar',
            unit: unitSymbols.length,
          })}
        >
          {(formElementId) => (
            <TextBox
              disabled={props.altitudeDisabled}
              errorMessage={Number.isNaN(Number.parseFloat(altitudeInput)) ? '' : undefined}
              id={formElementId()}
              step={0.1}
              type="number"
              value={altitudeInput}
              onInput={(e) => {
                setAltitudeInput(e.currentTarget.value);
                const altitude = e.currentTarget.valueAsNumber;
                if (!Number.isNaN(altitude)) {
                  ViewSettings.instance.setOrthophoto({ altitude });
                }
              }}
            />
          )}
        </LabelledContainer>
      )}
    </Stack>
  );
};

export { OrthophotoSettings };
